import Component from '@glimmer/component';
import { A } from '@ember/array';
import EmberObject, { action } from '@ember/object';
import { next } from '@ember/runloop';
import { tracked } from '@glimmer/tracking';
import { isNull } from 'lodash';
import { scheduleOnce, later, cancel } from '@ember/runloop';
import { inject as service } from '@ember/service';
import { addObserver, removeObserver } from '@ember/object/observers';
import { timeout, restartableTask } from 'ember-concurrency';

import SmartSearchApi from 'mewe/api/smart-search-api';
import GroupApi from 'mewe/api/group-api';
import Scrolling from 'mewe/utils/scrolling-utils';
import { minSmartSearchPhraseLength } from 'mewe/constants';
import JSONSerializer from 'mewe/utils/store-utils/serializers/json-serializer';
import { Post } from 'mewe/stores/models/post-model';
import Group from 'mewe/stores/models/group-model';
import SimpleGroup from 'mewe/stores/models/simple-group-model';
import MessageFactory from 'mewe/stores/models/chat-message-model';
import Page from 'mewe/stores/models/page-model';
import toDisplaySimpleHighlight from 'mewe/stores/text-parsers/to-display-simple-highlight';
import CurrentUserStore from 'mewe/stores/current-user-store';
import ChatStore from 'mewe/stores/chat-store';
import ThreadFactory from 'mewe/stores/models/thread-model';
import { handleOtherStores } from 'mewe/fetchers/fetch-feed';
import isUndefined from 'mewe/utils/isUndefined';
import { processMyContactsInGroups } from 'mewe/utils/group-utils-light';
import PS from 'mewe/utils/pubsub';
import Verbose from 'mewe/utils/verbose';
import { trackedInLocalStorage } from 'ember-tracked-local-storage';
import { FeedTypes } from 'mewe/constants';
import { isPresent } from '@ember/utils';
import PostApi from 'mewe/api/post-api';

const verbose = Verbose({ prefix: '[Search]' }).log;
const Thread = ThreadFactory(CurrentUserStore, ChatStore);
const Message = MessageFactory(CurrentUserStore, ChatStore, Thread);

const Serializer = JSONSerializer.create();

export default class MwSmartSearch extends Component {
  @service dynamicDialogs;
  @service analytics;
  @service account;
  @service router;

  @tracked phrase = '';
  @tracked lastPhraseValue = '';
  @tracked minLengthReached = false;
  @tracked isFetchingMore;
  @tracked resultPages = A();
  @tracked isSearchRequestPending = false;
  @tracked isAutocompleteRequestPending = false;
  @tracked category = 'members';
  @tracked isCheckingTopCategory = false;
  @tracked autocompletePhrase = '';
  @tracked autocompleteResults = [];
  @tracked showAutocomplete = false;
  @tracked showResults = false;
  @tracked overlayDidInsert;
  @tracked trendingHashtags = A();
  @trackedInLocalStorage({ keyName: 'autocomplete-phrase', defaultValue: [] })
  cachedAutocompletePhrase;

  @tracked hidePeopleTab = true;
  @tracked hideGroupsTab = true;
  @tracked hidePagesTab = true;
  @tracked hideChatsTab = true;
  @tracked hidePostsTab = true;

  scrolling = Scrolling();
  isLoading = false;
  currentTask = null;
  pageCount = 0;
  pageSize = 20;
  minPhraseLength = minSmartSearchPhraseLength;

  defaultParams = {
    highlightStart: `<span class='word-highlight'>`,
    highlightEnd: `</span>`,
  };

  @action
  onInsertSearch(element) {
    this.elementSearch = element;

    this.focusSmartSearchBind = this.focusSmartSearch.bind(this);
    this.closeSmartSearchBind = this.closeSmartSearch.bind(this);
    this.openSmartSearchBind = this.openSmartSearch.bind(this);
    PS.Sub('focus.smart.search', this.focusSmartSearchBind);
    PS.Sub('close.smart.search', this.closeSmartSearchBind);
    PS.Sub('open.smart.search', this.openSmartSearchBind);

    // intentionally used observer instead of handling actions from input (keyUp or other)
    // to keep track of any type of change in one place (including closing search by button or by clicking outside)
    addObserver(this, 'phrase', this.phraseChanged);
  }

  @action
  onDestroySearch() {
    PS.Unsub('focus.smart.search', this.focusSmartSearchBind);
    PS.Unsub('close.smart.search', this.closeSmartSearchBind);
    PS.Unsub('open.smart.search', this.openSmartSearchBind);

    removeObserver(this, 'phrase', this.phraseChanged);
  }

  @action
  onInsertResults() {
    this.resultsElement = document.querySelector(`.box-smart-search_results`);
  }

  @action
  onInsertOverlay() {
    next(() => this.overlayDidInsert = true);
  }

  @action
  onDestroyResults() {
    this.unbindScrollDown();
  }

  @action
  onDestroyDialog() {
    // dialog can be destroyed by clicking on some result and being redirected,
    // so when it's destroyed then make sure that autocomplete is not opened and then
    // call close function to retore state to initial value
    later(() => {
      if (!this.showAutocomplete && !this.showResults) {
        this.closeSmartSearch();
      }
    }, 100);
  }

  @action
  unbindScrollDown() {
    if (this.resultsElement) {
      this.scrolling.unbindScrollDown(this.resultsElement);
    }
  }

  get phraseValue() {
    return this.phrase.trim();
  }

  get reversedCachedList() {
    return this.cachedAutocompletePhrase.reverse().slice(0, 25);
  }

  get noCachedResults() {
    return !this.cachedAutocompletePhrase.some(item => item.isCached);
  }

  @restartableTask
  *autoCompletePhraseChanged(phrase) {
    const minLengthReached = phrase.length >= this.minPhraseLength;
    this.isAutocompleteRequestPending = minLengthReached;
    this.showAutocomplete = true;

    // wait 300ms only if minLength is reached, otherwise update steate immediately
    if (minLengthReached) {
      // if reached then update the state immediately and show loader during timeout
      this.minLengthReached = true;
      yield timeout(300);
    }

    try {
      if (minLengthReached) {
        let response = yield SmartSearchApi.search({ q: phrase });
        let options = response.length ? response.map((item) => ({ name: item, isCached: false })) : [];
        this.isAutocompleteRequestPending = false;
        this.autocompleteResults = options;
      } else {
        this.autocompleteResults = this.reversedCachedList;
        // if not reached then update state after setting cached results to avoid blinking states
        this.minLengthReached = false;
      }
    } catch (error) {
      // In case of error, just show cached results
      this.isAutocompleteRequestPending = false;
      this.autocompleteResults = [];
    }
  }

  @action
  keyDown(e) {
    if (e.keyCode === 13 || e.key === 'Enter') {
      this.updateAutoCompletePhrase({ name: this.autocompletePhrase});
    }
    if (e.keyCode === 27 || e.key === 'Escape') {
      document.activeElement.blur();
      this.closeSmartSearch();
    }
    next(() => {
      this.trackTyping();
    });
  }

  @action
  updateAutoCompletePhrase(phraseObject, event) {
    event?.stopPropagation();

    this.phrase = phraseObject.name;
    this.autocompletePhrase = phraseObject.name;
    phraseObject.isCached = true;

    // Check if the phrase already exists in cached phrases
    const isDuplicate = this.cachedAutocompletePhrase.some(
      cached => cached.name === phraseObject.name
    );

    // Only update if it's not a duplicate
    if (!isDuplicate && isPresent(phraseObject.name)) {
      this.cachedAutocompletePhrase = [...this.cachedAutocompletePhrase, phraseObject];
    }

    next(() => {
      this.trackTyping();
    });
  }

  @action
  removeCachedPhrase(phraseObject, event) {
    event.stopPropagation();
    this.cachedAutocompletePhrase = this.cachedAutocompletePhrase.filter(cached => cached.name !== phraseObject.name);
    this.autoCompletePhraseChanged.perform(this.autocompletePhrase);
  }

  @action
  removeAllCachedPhrase() {
    this.dynamicDialogs.openDialog('simple-dialog-new', {
      title: __('Clear recent searches?'),
      message: __('You won’t be able to undo this. If you clear your recent search history, you may still see accounts you’ve searched for as suggested results.'),
      okButtonText: __('Clear all'),
      onConfirm: () => {
        this.cachedAutocompletePhrase = [];
        this.autoCompletePhraseChanged.perform(this.autocompletePhrase);
        this.focusIn();
      },
      onCancel: () => {
        this.focusIn();
      },
    });
  }

  @action
  phraseChanged() {
    if (this.lastPhraseValue === this.phraseValue) {
      this.showAutocomplete = false;
      return;
    } else {
      this.lastPhraseValue = this.phraseValue;
    }

    this.clearArrays();

    if (this.phraseValue.length >= this.minPhraseLength) {
      this.isSearchRequestPending = true;
      this.showAutocomplete = false;
      this.showResults = true;
      this.throttleSearch();
    } else {
      this.isSearchRequestPending = false;
      this.showResults = false;
    }
  }

  throttleSearch() {
    if (this.scheduleId) {
      cancel(this.scheduleId);
    }

    // here we call a search for a new phrase, which should be done for 'members' category
    // because it's the default one, but also 'top' results have to be fetched to know if other tabs should be hidden
    this.category = 'members';
    this.hidePeopleTab = true;
    this.hideGroupsTab = true;
    this.hidePagesTab = true;
    this.hideChatsTab = true;
    this.hidePostsTab = true;

    this.pageCount = 0;

    this.scheduleId = later(
      this,
      () => {
        if (!this.isDestroyed && !this.isDestroying) {
          this.startNewSearch();
        }
      },
      350
    );
  }

  get hideTabs() {
    if (this.isCheckingTopCategory) return true;
    return this.hidePeopleTab &&
      this.hideGroupsTab &&
      this.hidePagesTab &&
      this.hideChatsTab &&
      this.hidePostsTab;
  }

  // new search does two requests:
  // 1. "top" results to check which tabs have any results and which should be hidden
  // 2. parse the response of "top" results right away to display up the 3 results that it provides
  // 3. do follow up request to get full page of results for the initial tab
  startNewSearch() {
    this.isCheckingTopCategory = true;

    SmartSearchApi.top({ query: this.phraseValue, limit: 1 })
      .then((response) => {
        this.hidePeopleTab = !response.results?.find((result) => result.user);
        this.hideGroupsTab = !response.results?.find((result) => result.group);
        this.hidePagesTab = !response.results?.find((result) => result.page);
        this.hideChatsTab = !response.results?.find((result) => result.chatMessage) && !response.results?.find((result) => result.chatThread);
        this.hidePostsTab = !response.results?.find((result) => result.post);

        if (!this.hidePeopleTab) {
          this.category = 'members';
        } else if (!this.hidePostsTab) {
          this.category = 'posts';
        } else if (!this.hideGroupsTab) {
          this.category = 'groups';
        } else if (!this.hidePagesTab) {
          this.category = 'pages';
        } else if (!this.hideChatsTab) {
          this.category = 'chats';
        } else {
          this.category = null;
        }

        this.parseResponse(response, false);

        if (this.category) {
          this.search();
        }

        this.isCheckingTopCategory = false;
      });
  }

  trackTyping() {
    // track typing once per "search session", when at least one character is typed in.
    // "session" ends when input is CLEARED AND UNFOCUSED
    if (!this.wasTypingTracked && this.autocompletePhrase.length) {
      const theme = this.args.model.theme || '';
      let context = 'others';

      if (theme === 'contacts') context = 'home';
      else if (theme === 'store') context = 'store';
      else if (theme === 'profile') context = 'profile';
      else if (theme === 'privacymail') context = 'chats';
      else if (~theme.indexOf('page')) context = 'pages'; // indexOf to catch both pages/page
      else if (~theme.indexOf('group')) context = 'groups'; // indexOf to catch both groups/group
      else if (~theme.indexOf('event')) context = 'events'; // indexOf to catch both events/event

      this.analytics.sendEvent('typingInSearchStarted', {
        context: context,
      });
      this.wasTypingTracked = true;

      verbose(`Typing in Search started in context: ${context}`);
    }
  }

  get noMatches() {
    if (this.category === 'trending') return false;
    if (!this.phraseValue) return false;

    return this.resultPages.every((page) => {
      return page.noResults;
    });
  }

  get searchData() {
    return {
      searchTerm: this.phraseValue,
      highlightStart: this.defaultParams.highlightStart,
      highlightEnd: this.defaultParams.highlightEnd,
    };
  }

  search(isFetchingMore) {
    if (!this.category) return;
    let phrase = this.phraseValue;
    const doRequest = this.getEndpoint();
    let params = {
      query: phrase,
      highlighting: this.category === 'chats',
      withChatThreadsData: true
    };

    if (!isFetchingMore) {
      this.pageCount = 0;
    }

    // jshint ignore: start
    params = {
      ...params,
      ...this.getPaginationParams(isFetchingMore),
    };
    // jshint ignore: end

    if (phrase.length >= this.minPhraseLength) {
      this.currentTask = doRequest(params);

      this.currentTask
        .then((response) => {
          if (this.isDestroyed || this.isDestroying) return;
          if (!this.args.isSmartSearchActive) return;
          this.parseResponse(response, isFetchingMore);
        })
        .finally(() => {
          this.currentTask = null;
        });
    }
  }

  getEndpoint() {
    const request = SmartSearchApi[this.category];
    return request.bind(SmartSearchApi);
  }

  handleNoResultsInResponse(isFetchMore) {
    if (typeof isFetchMore !== 'undefined' && isFetchMore !== null) {
      if (isFetchMore) {
        this.hasMoreResults = false;
      } else {
        this.clearArrays();
        this.hasMoreResults = false;
      }
    } else {
      this.clearArrays();
      this.hasMoreResults = false;
    }
  }

  parseResponse({ hasMoreResults, results, relatedPosts = A(), postsData = {}, chatThreadsData = A() }, isFetchMore) {
    if (this.isDestroyed || this.isDestroying) return;

    // filter out comments becasue we dont want to show comments any more
    results = results.filter(result => !result.hasOwnProperty('comment'));

    // sometimes BE returns incorrect hasMoreResults and we overwrite it on our side (MW-4153)
    if (hasMoreResults && !results.length) {
      hasMoreResults = false;
    }

    handleOtherStores(postsData);

    if (results.length === 0 && this.pageCount === 0) {
      this.handleNoResultsInResponse(isFetchMore);
    } else {
      const currentCategory = this.category;

      let resultPages = this.resultPages;

      let posts = A();
      let users = A();
      let groups = A();
      let chats = A(); // chatMessage + chatThread
      let comments = A();
      let pages = A();

      let i;
      for (let i = 0; i < results.length; i++) {
        let current = results[i];

        if (current.hasOwnProperty('post')) {
          posts.pushObject(current.post);
        } else if (current.hasOwnProperty('comment')) {
          comments.pushObject(current.comment);
        } else if (current.hasOwnProperty('user')) {
          const user = this.parseTextToHighlight(current.user);
          users.pushObject(user);
        } else if (current.hasOwnProperty('group')) {
          let groupModel = Serializer.deserializeOne(Group, this.parseTextToHighlight(current.group));
          groups.pushObject(groupModel);
        } else if (current.hasOwnProperty('chatMessage')) {
          if (current.chatMessage.textPlain !== '') {
            chats.pushObject(this.chatMessageEnrichment(current.chatMessage, chatThreadsData));
          }
        } else if (current.hasOwnProperty('chatThread')) {
          chats.pushObject(this.chatThreadEnrichment(current.chatThread));
        } else if (current.hasOwnProperty('page')) {
          pages.pushObject(Serializer.deserializeOne(Page, this.parseTextToHighlight(current.page)));
        }
      }

      for (i = 0; i < comments.length; i++) {
        var current = comments[i];

        let matchIdx;
        if ((matchIdx = this.matchIndexIn(posts, current.postItemId)) !== -1) {
          typeof posts[matchIdx].comments === 'undefined' // TODO - extract into a separate function
            ? (posts[matchIdx].comments = { feed: [current] })
            : posts[matchIdx].comments.feed.push(current);

          posts[matchIdx].initialCommentsSet = true; //need for comments button to not load comments on click
        } else if ((matchIdx = this.matchIndexInRelated(relatedPosts, current.postItemId)) !== -1) {
          typeof relatedPosts[matchIdx].post.comments === 'undefined' // .post. because of a structure of relatedPosts from BE
            ? (relatedPosts[matchIdx].post.comments = { feed: [current], canBeMoreAfter: true, canBeMoreBefore: true })
            : relatedPosts[matchIdx].post.comments.feed.push(current);

          relatedPosts[matchIdx].post.initialCommentsSet = true; //need for comments button to not load comments on click
        } else {
          // TODO - this case should be logged, as normally it should never happen.
          // that means we got an comment without a related post data
        }
      }

      const filteredPosts = relatedPosts.filter((postRecord) => {
        return posts.findIndex(({ postItemId }) => postItemId === postRecord.post.postItemId) === -1;
      });

      posts.pushObjects(filteredPosts);

      posts = Serializer.deserializeAll(Post, posts);

      let privacymailPosts = A(posts.filter((post) => post.scope === 'privacymail'));

      posts = A(posts.filter((post) => post.scope !== 'privacymail'));

      let page = EmberObject.extend({
        members: users,
        groups: groups,
        pages: pages,
        chats: chats,
        chatPosts: privacymailPosts,
        posts: posts,
        noResults: !posts?.length && !privacymailPosts?.length && !users?.length && !groups?.length && !pages?.length && !chats?.length,
      }).create();

      if (!isFetchMore) {
        resultPages.clear();
        resultPages.pushObject(page);
        this.resultPages = resultPages;
      } else {
        if (resultPages[0] && resultPages[0][currentCategory]) {
          resultPages[0][currentCategory].pushObjects(page[currentCategory]);
        }
        if (currentCategory === 'chats') {
          resultPages[0]['chatPosts'].pushObjects(page['chatPosts']);
        }
      }

      if (hasMoreResults) {
        scheduleOnce('afterRender', this, () => {
          if (this.isDestroying || this.isDestroyed) return;

          if (this.resultsElement) {
            this.scrolling.bindScrollDownElement(this.resultsElement, () => {
              this.fetchMoreResults();
            });
          }
        });
      } else {
        this.unbindScrollDown();
      }

      // TODO - correction - empty chat messages - unless BE is ready:
      if (posts.length === 0 && users.length === 0 && groups.length === 0 && chats.length === 0 && pages.length === 0) {
        this.handleNoResultsInResponse(isFetchMore);
      }

      this.fetchGroupContactsForResults(page);
    }

    this.hasMoreResults = hasMoreResults;
    this.pageCount = this.pageCount + 1;
    this.isSearchRequestPending = false;
    this.isFetchingMore = false;
  }

  parseTextToHighlight(object) {
    const nameParts = A(object.nameParts).filterBy('isHighlighted');
    object.nameDisplay = toDisplaySimpleHighlight(object.name, undefined, { textPartsHighlighted: nameParts });
    return object;
  }

  matchIndexIn(posts, expectedPostItemId) {
    return posts.findIndex(({ postItemId }) => {
      return postItemId === expectedPostItemId;
    });
  }

  matchIndexInRelated(posts, expectedPostItemId) {
    return posts.findIndex(({ post }) => {
      return post.postItemId === expectedPostItemId;
    });
  }

  clearArrays() {
    if (this.resultPages) {
      this.resultPages.clear();
    }
  }

  getPaginationParams(isFetchingMore) {
    const category = this.category;
    const pageSize = this.pageSize;
    const pageCount = this.pageCount;
    const newPaginationApis = ['groups', 'members'];

    if (newPaginationApis.indexOf(category) !== -1) {
      if (this.pageCount > 0) {
        return {
          // NEW pagination style
          page: pageCount + 1,
          pageSize,
        };
      }
    } else {
      let resultPages = this.resultPages;
      let resultsLength = 0;
      if (resultPages && resultPages[0] && category === 'chats') {
        resultsLength = A([...resultPages[0]['chatPosts'], ...resultPages[0]['chats']]).length;
      } else {
        if (resultPages && resultPages[0] && resultPages[0][category]) resultsLength = resultPages[0][category].length;
      }

      return {
        // OLD pagination style
        limit: pageSize,
        offset: isFetchingMore ? resultsLength : 0 // I see that number of results is sometimes random so count offset basing on results.length
      };
    }
  }

  chatThreadEnrichment(chatThread) {
    let thread = {
      ...chatThread,

      id: chatThread.threadId,
      recordType: 'chatThread',
      query: this.phraseValue,
      highlightStart: this.defaultParams.highlightStart,
      highlightEnd: this.defaultParams.highlightEnd,
      isFromSearch: true,
    };

    return Serializer.deserializeOne(Thread, thread);
  }

  chatMessageEnrichment(chatMessage, relatedThreads = A()) {
    chatMessage.text = chatMessage.textPlain;
    chatMessage.recordType = 'chatMessage';

    chatMessage = Serializer.deserializeOne(Message, chatMessage);

    if (chatMessage.group) {
      // serizlize group to compute avatar link
      chatMessage.group = Serializer.deserializeOne(SimpleGroup, chatMessage.group);
    }

    const threadMatch = relatedThreads.find(thread => thread.threadId === chatMessage.threadId);

    if (threadMatch) {
      threadMatch.id = threadMatch.threadId;
      chatMessage.thread = Serializer.deserializeOne(Thread, threadMatch);
    }

    return chatMessage;
  }

  fetchGroupContactsForResults(results) {
    // if there are any "group" results in search then do additional
    // request to get number of contacts in those groups
    if (results?.groups?.length) {
      const groupIds = results.groups.map((g) => g.id).join(',');

      GroupApi.myContactsInGroups({ groupIds: groupIds }).then((data) => {
        if (this.isDestroyed || this.isDestroying) return;

        processMyContactsInGroups(results.groups, data);
      });
    }
  }

  focusSmartSearch() {
    const input = this.elementSearch.querySelector('.h-input_search');
    input.focus();
    // nice blinking indication where the cursor was focused - used when click on Search members in FTUE dialog
    setTimeout(() => {
      input.classList.add('highlight');
    }, 200);
    setTimeout(() => {
      input.classList.remove('highlight');
    }, 600);
  }

  doSearch(selected) {
    this.isSearchRequestPending = true;
    this.category = selected;
    this.unbindScrollDown();
    this.clearArrays();
    this.focusOut();
    this.search();
  }

  async fetchTrendingHashtags() {
    const response = await PostApi.getTrendingHashTags();
    this.isSearchRequestPending = false;
    this.trendingHashtags.clear();
    this.trendingHashtags.pushObjects(response.hashtags);
  }

  @action
  openSmartSearch() {
    next(() => {
      this.elementSearch.querySelector('.h-input_search').focus();
    });
  }

  @action
  closeSmartSearch() {
    this.overlayDidInsert = false;

    later(() => {
      this.autocompleteResults = [];
      this.isAutocompleteRequestPending = false;
      this.resultPages = A();
      this.autoCompletePhraseChanged.cancelAll();
      this.showAutocomplete = false;
      this.showResults = false;
      this.lastPhraseValue = '';
      this.autocompletePhrase = '';
      this.phrase = '';
      this.hasFocus = false;
      this.args.setSmartSearchState(false);
      this.minLengthReached = false;

      this.category = 'members';
      this.hidePeopleTab = true;
      this.hideGroupsTab = true;
      this.hidePagesTab = true;
      this.hideChatsTab = true;
      this.hidePostsTab = true;

      if (!this.phraseValue.length && !this.hasFocus && this.wasTypingTracked) {
        this.wasTypingTracked = false;
        verbose(`Typing session cleared`);
      }
    }, 100);
  }

  @action
  wrapperClick(e) {
    if (e.target.classList.contains('dialog_inner-wrapper')) {
      this.closeSmartSearch();
    }
  }

  @action
  focusIn() {
    this.showAutocomplete = true;

    if (!this.minLengthReached) {
      this.autocompleteResults = this.reversedCachedList;
    }

    this.args.setSmartSearchState(true);
    this.hasFocus = true;
  }

  @action
  focusOut() {
    this.hasFocus = false;
    // focusOut ends typing session
    this.trackTyping();
  }

  @action
  fetchMoreResults() {
    this.isFetchingMore = true;
    this.search(true);
  }

  @action
  select(selected) {
    if (isUndefined(selected) || isNull(selected)) return;

    if (this.currentTask) {
      this.currentTask.catch(() => this.doSearch(selected));

      return this.currentTask.reject();
    }

    this.doSearch(selected);
  }

  @action
  selectTag(hashtag, index) {
    this.closeSmartSearch();
    this.router.transitionTo('app.myworld.index', { queryParams: { scope: FeedTypes.HASHTAG, tag: hashtag.name } });
    const position = index + 1;
    this.analytics.sendEvent('trendingHashtagClicked', { hashtag: hashtag.name, position, posts_count: hashtag.total, context: 'search' });
  }

  @action
  onRemovePost(post) {
    this.resultPages.forEach((page) => {
      const postInPosts = page.posts.findBy('postItemId', post.postItemId);
      if (postInPosts) {
        page.posts.removeObject(postInPosts);
      }

      const postInChatPosts = page.chatPosts.findBy('postItemId', post.postItemId);
      if (postInChatPosts) {
        page.chatPosts.removeObject(postInChatPosts);
      }

    });

    this.resultPages = A([...this.resultPages]);
  }

  @action
  onInsertHashtags() {
    this.fetchTrendingHashtags();
  }
}
